/**
 * GlassChatPill — Persistent floating copilot pill with progressive disclosure.
 *
 * States:
 *   1. Hidden      — not mounted / display:none
 *   2. Ambient     — small circle with static avatar photo (no text)
 *   3. Named       — avatar + staff name (shown after card has been seen)
 *   4. Suggestion  — avatar video plays, suggestion text + "Let's talk" + dismiss
 *   5. Connecting  — avatar photo + "Connecting..." text
 *
 * Transitions:
 *   t=0       → hidden (clean page load)
 *   t=5s      → ambient (small avatar circle fades in)
 *   card seen → named (avatar + name)
 *   VLM msg   → suggestion (video plays, text expands, avatar pulses)
 *   dismiss   → named (reverts)
 *   click CTA → connecting
 *   video rdy → hidden
 *   conv end  → named (if card not visible) or hidden (if card visible)
 */

import { createGlassPanel, createGlassButton, glassText } from '../glass'
import { springEnter, springExit, type SpringAnimation } from '../spring'
import { injectTokens, tokens } from '../tokens'
import { autoDetectAndApply } from '../themeDetector'

export type PillState = 'hidden' | 'ambient' | 'named' | 'suggestion' | 'connecting'

const PULSE_KEYFRAMES = `
@keyframes hs-pill-pulse {
  0%, 100% { box-shadow: 0 0 0 0 var(--hs-accent-glow); }
  50% { box-shadow: 0 0 14px 5px var(--hs-accent-glow); }
}
`

export class GlassChatPill {
  private el: HTMLDivElement | null = null
  private avatarWrap: HTMLDivElement | null = null
  private avatarImg: HTMLImageElement | null = null
  private avatarVideo: HTMLVideoElement | null = null
  private nameEl: HTMLSpanElement | null = null
  private suggestionArea: HTMLDivElement | null = null
  private suggestionText: HTMLSpanElement | null = null
  private currentAnim: SpringAnimation | null = null
  private _state: PillState = 'hidden'
  private pulseStyleInjected = false
  private avatarUrl: string | null = null
  private videoUrl: string | null = null
  private staffName = ''

  // Callbacks
  onClick: (() => void) | null = null
  onSuggestionAccept: (() => void) | null = null
  onSuggestionDismiss: (() => void) | null = null
  /** Fires on long-press (mouse/touch held ≥ LONG_PRESS_MS) — used for the "quiet this visit" gesture. */
  onLongPress: (() => void) | null = null

  /** Build the pill DOM (once). Must call a transition method to make it visible. */
  init(avatarUrl: string | null, videoUrl: string | null, staffName: string): void {
    if (this.el) return
    injectTokens()

    this.avatarUrl = avatarUrl
    this.videoUrl = videoUrl
    this.staffName = staffName

    this.el = createGlassPanel({
      radius: 'pill',
      elevation: 'elevated',
      interactive: true,
      className: 'hs-chat-pill',
    }) as HTMLDivElement

    Object.assign(this.el.style, {
      position: 'fixed',
      bottom: 'var(--hs-space-5)',
      left: '0',
      right: '0',
      margin: '0 auto',
      width: 'fit-content',
      maxWidth: 'min(90vw, 600px)',
      display: 'none',
      alignItems: 'center',
      gap: '0px',
      padding: '5px',
      zIndex: tokens.zControls,
      cursor: 'pointer',
      userSelect: 'none',
      opacity: '0',
      boxShadow: '0 4px 24px rgba(0, 0, 0, 0.18)',
      transition: 'gap 0.3s ease, padding 0.3s ease, box-shadow 0.2s ease',
      overflow: 'hidden',
    })

    autoDetectAndApply(this.el)

    // Hover effect
    this.el.addEventListener('mouseenter', () => {
      if (this.el) this.el.style.boxShadow = '0 6px 32px rgba(0, 0, 0, 0.24)'
    })
    this.el.addEventListener('mouseleave', () => {
      if (this.el) this.el.style.boxShadow = '0 4px 24px rgba(0, 0, 0, 0.18)'
    })

    // --- Avatar wrapper (holds both photo and video) ---
    this.avatarWrap = document.createElement('div')
    Object.assign(this.avatarWrap.style, {
      width: '36px',
      height: '36px',
      borderRadius: '50%',
      overflow: 'hidden',
      flexShrink: '0',
      position: 'relative',
      border: '1.5px solid var(--hs-glass-border)',
      transition: 'box-shadow 0.3s ease, width 0.3s ease, height 0.3s ease',
    })

    // Static avatar image
    if (avatarUrl) {
      this.avatarImg = document.createElement('img')
      this.avatarImg.src = avatarUrl
      this.avatarImg.alt = staffName
      Object.assign(this.avatarImg.style, {
        width: '100%',
        height: '100%',
        objectFit: 'cover',
        display: 'block',
      })
      this.avatarWrap.appendChild(this.avatarImg)
    } else {
      // Fallback circle
      const circle = document.createElement('div')
      Object.assign(circle.style, {
        width: '100%',
        height: '100%',
        background: 'var(--hs-accent)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 'var(--hs-text-sm)',
        fontWeight: 'var(--hs-weight-semibold)',
        color: 'var(--hs-accent-text)',
      })
      circle.textContent = staffName.charAt(0).toUpperCase()
      this.avatarWrap.appendChild(circle)
    }

    // Video element (hidden until suggestion, plays idle video loop)
    if (videoUrl) {
      this.avatarVideo = document.createElement('video')
      this.avatarVideo.src = videoUrl
      this.avatarVideo.muted = true
      this.avatarVideo.loop = true
      this.avatarVideo.playsInline = true
      this.avatarVideo.setAttribute('playsinline', '')
      Object.assign(this.avatarVideo.style, {
        position: 'absolute',
        inset: '0',
        width: '100%',
        height: '100%',
        objectFit: 'cover',
        display: 'none',
      })
      this.avatarWrap.appendChild(this.avatarVideo)
    }

    this.el.appendChild(this.avatarWrap)

    // --- Name label (hidden in ambient state) ---
    this.nameEl = document.createElement('span')
    glassText(this.nameEl, 'primary', 'base', 'medium')
    this.nameEl.textContent = staffName
    Object.assign(this.nameEl.style, {
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      maxWidth: '0',
      opacity: '0',
      transition: 'max-width 0.3s ease, opacity 0.25s ease, margin 0.3s ease',
      marginLeft: '0',
    })
    this.el.appendChild(this.nameEl)

    // --- Suggestion area (hidden until suggestion) ---
    this.suggestionArea = document.createElement('div')
    Object.assign(this.suggestionArea.style, {
      display: 'flex',
      alignItems: 'center',
      gap: 'var(--hs-space-2)',
      maxWidth: '0',
      opacity: '0',
      overflow: 'hidden',
      transition: 'max-width 0.35s ease, opacity 0.25s ease, margin 0.3s ease',
      marginLeft: '0',
    })

    // Separator
    const sep = document.createElement('span')
    Object.assign(sep.style, {
      width: '1px',
      height: '20px',
      background: 'var(--hs-divider)',
      flexShrink: '0',
    })
    this.suggestionArea.appendChild(sep)

    // Suggestion text
    this.suggestionText = document.createElement('span')
    glassText(this.suggestionText, 'secondary', 'sm', 'normal')
    Object.assign(this.suggestionText.style, {
      whiteSpace: 'normal',
      lineHeight: '1.4',
      display: '-webkit-box',
      WebkitLineClamp: '2',
      WebkitBoxOrient: 'vertical',
      overflow: 'hidden',
    })
    this.suggestionArea.appendChild(this.suggestionText)

    // "Let's talk" CTA
    const ctaBtn = createGlassButton({
      label: "Let's talk",
      variant: 'accent',
      size: 'sm',
      onClick: (e) => {
        e.stopPropagation()
        this.onSuggestionAccept?.()
      },
    })
    ctaBtn.style.flexShrink = '0'
    this.suggestionArea.appendChild(ctaBtn)

    // Dismiss X
    const dismissBtn = document.createElement('button')
    dismissBtn.textContent = '\u2715'
    Object.assign(dismissBtn.style, {
      background: 'none',
      border: 'none',
      color: 'var(--hs-text-tertiary)',
      fontSize: '12px',
      cursor: 'pointer',
      padding: '4px',
      flexShrink: '0',
      lineHeight: '1',
      transition: 'color 0.15s ease',
    })
    dismissBtn.addEventListener('mouseenter', () => { dismissBtn.style.color = 'var(--hs-text-primary)' })
    dismissBtn.addEventListener('mouseleave', () => { dismissBtn.style.color = 'var(--hs-text-tertiary)' })
    dismissBtn.addEventListener('click', (e) => {
      e.stopPropagation()
      this.onSuggestionDismiss?.()
      this.clearSuggestion()
    })
    this.suggestionArea.appendChild(dismissBtn)

    this.el.appendChild(this.suggestionArea)

    // Click on pill body → start conversation.
    // Long-press (≥ 700ms) → fire onLongPress (used for "quiet this visit"
    // gesture). We suppress the subsequent click when a long-press fired
    // so the visitor doesn't accidentally also open a conversation.
    let pressTimer: ReturnType<typeof setTimeout> | null = null
    let longPressed = false
    const LONG_PRESS_MS = 700
    const startPress = (e: Event) => {
      if ((e.target as HTMLElement).closest('button')) return
      longPressed = false
      if (pressTimer) clearTimeout(pressTimer)
      pressTimer = setTimeout(() => {
        longPressed = true
        pressTimer = null
        this.onLongPress?.()
      }, LONG_PRESS_MS)
    }
    const cancelPress = () => {
      if (pressTimer) { clearTimeout(pressTimer); pressTimer = null }
    }
    this.el.addEventListener('pointerdown', startPress)
    this.el.addEventListener('pointerup', cancelPress)
    this.el.addEventListener('pointerleave', cancelPress)
    this.el.addEventListener('pointercancel', cancelPress)
    this.el.addEventListener('click', (e) => {
      if (longPressed) { longPressed = false; return }
      if (!(e.target as HTMLElement).closest('button')) {
        this.onClick?.()
      }
    })

    // Append to <html> to escape any body transforms (e.g. GSAP scroll rigs)
    // that would break position:fixed positioning.
    document.documentElement.appendChild(this.el)
  }

  // -----------------------------------------------------------------------
  // State transitions
  // -----------------------------------------------------------------------

  /** Show as small avatar circle only (ambient presence). */
  showAmbient(): void {
    if (!this.el || this._state === 'ambient') return
    this._state = 'ambient'

    // Collapse name and suggestion
    this.collapseName()
    this.collapseSuggestion()
    this.stopAvatarVideo()

    this.el.style.display = 'flex'
    this.el.style.gap = '0px'
    this.el.style.padding = '5px'

    this.currentAnim?.stop()
    this.currentAnim = springEnter(this.el, {
      preset: 'gentle',
      from: { opacity: 0, scale: 0.8, y: 12 },
    })
  }

  /** Show avatar + name (after card has been seen). */
  showNamed(): void {
    if (!this.el) return
    if (this._state === 'named') return
    const wasHidden = this._state === 'hidden'
    this._state = 'named'

    this.collapseSuggestion()
    this.stopAvatarVideo()
    this.expandName()

    this.el.style.display = 'flex'
    this.el.style.gap = 'var(--hs-space-3)'
    this.el.style.padding = '8px 20px 8px 8px'

    if (wasHidden) {
      this.currentAnim?.stop()
      this.el.style.opacity = '0'
      this.currentAnim = springEnter(this.el, {
        preset: 'bouncy',
        from: { opacity: 0, scale: 0.85, y: 16 },
      })
    }
  }

  /** Show suggestion (avatar video plays, text + CTA expand). */
  showSuggestion(message: string): void {
    if (!this.el || !this.suggestionText) return
    const wasHidden = this._state === 'hidden'
    this._state = 'suggestion'

    // Ensure pill is visible — hide name to make room for suggestion text
    this.el.style.display = 'flex'
    this.el.style.gap = 'var(--hs-space-3)'
    this.el.style.padding = '10px 16px 10px 10px'
    this.el.style.borderRadius = 'var(--hs-radius-lg)'  // rounded rect, not capsule
    this.collapseName()

    if (wasHidden) {
      this.currentAnim?.stop()
      this.el.style.opacity = '0'
      this.currentAnim = springEnter(this.el, {
        preset: 'bouncy',
        from: { opacity: 0, scale: 0.9, y: 12 },
      })
    }

    // Start avatar video (animated idle loop)
    this.startAvatarVideo()
    this.startAvatarPulse()

    // Expand suggestion text
    this.suggestionText.textContent = message
    this.suggestionText.title = message
    requestAnimationFrame(() => {
      if (this.suggestionArea) {
        this.suggestionArea.style.maxWidth = '500px'
        this.suggestionArea.style.opacity = '1'
        this.suggestionArea.style.marginLeft = 'var(--hs-space-2)'
      }
    })
  }

  /** Clear suggestion, revert to named state. */
  clearSuggestion(): void {
    if (!this.el) return
    this._state = 'named'
    this.stopAvatarPulse()
    this.stopAvatarVideo()
    this.collapseSuggestion()
    this.expandName()
    this.el.style.padding = '8px 20px 8px 8px'
    this.el.style.borderRadius = 'var(--hs-radius-pill)'  // back to capsule
  }

  /** Show "Connecting..." state. */
  showConnecting(): void {
    if (!this.el || !this.nameEl) return
    this._state = 'connecting'

    this.stopAvatarPulse()
    this.stopAvatarVideo()
    this.collapseSuggestion()
    this.expandName()

    this.nameEl.textContent = 'Connecting...'
    this.nameEl.style.opacity = '0.6'

    this.el.style.display = 'flex'
    this.el.style.gap = 'var(--hs-space-3)'
    this.el.style.padding = '8px 20px 8px 8px'

    if (this.el.style.opacity === '0') {
      this.currentAnim?.stop()
      this.currentAnim = springEnter(this.el, {
        preset: 'snappy',
        from: { opacity: 0, scale: 0.9, y: 8 },
      })
    }
  }

  /** Restore name after connecting (e.g. if connection fails). */
  restoreName(): void {
    if (!this.nameEl) return
    this.nameEl.textContent = this.staffName
    this.nameEl.style.opacity = '1'
    // Don't set _state here — showNamed() needs to detect the transition from hidden
  }

  /** Hide the pill with animation. */
  hide(): void {
    this.currentAnim?.stop()
    if (!this.el) return
    this._state = 'hidden'

    this.stopAvatarPulse()
    this.stopAvatarVideo()

    const el = this.el
    this.currentAnim = springExit(el, {
      remove: false,
      preset: 'smooth',
      to: { opacity: 0, scale: 0.9, y: 12 },
      onComplete: () => { el.style.display = 'none' },
    })
  }

  /** Remove from DOM entirely. */
  destroy(): void {
    this.currentAnim?.stop()
    this.stopAvatarPulse()
    this.stopAvatarVideo()
    this.el?.remove()
    this.el = null
    this._state = 'hidden'
  }

  get state(): PillState { return this._state }
  get isVisible(): boolean { return this._state !== 'hidden' }

  // -----------------------------------------------------------------------
  // Private — expand/collapse helpers
  // -----------------------------------------------------------------------

  private expandName(): void {
    if (!this.nameEl) return
    this.nameEl.textContent = this.staffName
    this.nameEl.style.opacity = '1'
    this.nameEl.style.maxWidth = '150px'
    this.nameEl.style.marginLeft = 'var(--hs-space-2)'
  }

  private collapseName(): void {
    if (!this.nameEl) return
    this.nameEl.style.maxWidth = '0'
    this.nameEl.style.opacity = '0'
    this.nameEl.style.marginLeft = '0'
  }

  private collapseSuggestion(): void {
    if (!this.suggestionArea) return
    this.suggestionArea.style.maxWidth = '0'
    this.suggestionArea.style.opacity = '0'
    this.suggestionArea.style.marginLeft = '0'
  }

  // -----------------------------------------------------------------------
  // Private — avatar video + pulse
  // -----------------------------------------------------------------------

  private startAvatarVideo(): void {
    if (!this.avatarVideo || !this.avatarImg) return
    this.avatarVideo.style.display = 'block'
    this.avatarVideo.currentTime = 0
    this.avatarVideo.play().catch(() => {})
    this.avatarImg.style.opacity = '0'
  }

  private stopAvatarVideo(): void {
    if (!this.avatarVideo || !this.avatarImg) return
    this.avatarVideo.pause()
    this.avatarVideo.style.display = 'none'
    this.avatarImg.style.opacity = '1'
  }

  private startAvatarPulse(): void {
    if (!this.avatarWrap) return
    this.injectPulseStyle()
    this.avatarWrap.style.animation = 'hs-pill-pulse 1.5s ease-in-out infinite'
  }

  private stopAvatarPulse(): void {
    if (!this.avatarWrap) return
    this.avatarWrap.style.animation = ''
  }

  private injectPulseStyle(): void {
    if (this.pulseStyleInjected) return
    this.pulseStyleInjected = true
    const style = document.createElement('style')
    style.textContent = PULSE_KEYFRAMES
    document.head.appendChild(style)
  }
}
